import { FunctionComponent, VNode } from 'preact';
import clsx from 'clsx';
import { useImage } from 'utilities/content';

import { FallbackImage } from 'components/FallbackImage';
import { Image } from 'components/Image';

import { Controls } from './components/Controls';
import { useSlider } from './utilities/use-slider';

import styles from './ImageSlider.module.scss';

interface ImageSliderProps {
  images: string[];
  children?: VNode;
  maxHeight?: string;
  initialSlide?: number;
}

export const ImageSlider: FunctionComponent<ImageSliderProps> & {
  Controls: typeof Controls;
} = function ImageSlider({
  images,
  children,
  maxHeight,
  initialSlide,
}: ImageSliderProps) {
  const { sliderRef, thumbnailRef, prev, next, hasNext, hasPrev } =
    useSlider(initialSlide);
  const { getImageUrl } = useImage();

  const slidesMarkup = images.map((image) => {
    const source = getImageUrl(image);

    return (
      <div key={image} className={styles.Slide}>
        <Image saveRatio blurBackground source={source} alt="" />
      </div>
    );
  });

  const thumbnailsSliderMarkup = images.length > 1 && (
    <div ref={thumbnailRef} className={clsx(styles.Slider, styles.Thumbnails)}>
      {images.map((image, index) => {
        const source = getImageUrl(image, '120');
        const fallback = getImageUrl(image);

        return (
          <div key={index} className={clsx(styles.Slide, styles.Thumbnail)}>
            <FallbackImage
              fallback={fallback}
              width={80}
              height={80}
              className={clsx(styles.SlideImage, styles.ThumbnailImage)}
              src={source}
              loading="lazy"
            />
          </div>
        );
      })}
    </div>
  );

  const controlsMarkup = children ? (
    children
  ) : (
    <Controls
      prevAction={{
        disabled: !hasPrev,
        onAction: prev,
      }}
      nextAction={{
        disabled: !hasNext,
        onAction: next,
      }}
    />
  );

  return (
    <div
      className={styles.ImageSlider}
      style={{ '--slide-max-height': maxHeight ?? 'none' }}
    >
      <div ref={sliderRef} className={styles.Slider}>
        {slidesMarkup}
      </div>
      {controlsMarkup}
      {thumbnailsSliderMarkup}
    </div>
  );
};

ImageSlider.Controls = Controls;
